import React, { useState } from 'react';
import { updateDefaultDuration, updateDurationOptions } from '../api';
import detailMessage from '../detailMessage';
import { useLang } from '../LangContext';

interface Props {
  token: string;
  initialOptions: number[];
  initialDefault: number | null;
  onOptionsChange?: (options: number[]) => void;
  onDefaultChange?: (value: number | null) => void;
}

const DurationSettings = ({
  token,
  initialOptions,
  initialDefault,
  onOptionsChange,
  onDefaultChange,
}: Props) => {
  const { t } = useLang();
  const [options, setOptions] = useState<number[]>(Array.isArray(initialOptions) ? initialOptions : []);
  const [defaultDuration, setDefaultDuration] = useState<number | null>(
    typeof initialDefault === 'number' ? initialDefault : null,
  );
  const [newDuration, setNewDuration] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const persistOptions = async (next: number[]) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await updateDurationOptions(next, token);
      setOptions(res.duration_options);
      onOptionsChange?.(res.duration_options);
      if ('default_duration' in res) {
        setDefaultDuration(res.default_duration);
        onDefaultChange?.(res.default_duration);
      }
      setSuccess(t.durationUpdated);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(detailMessage(err, t.durationFailedUpdate));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const parsed = parseFloat(newDuration);
    if (Number.isNaN(parsed) || parsed < 0.5 || parsed > 8.0) {
      setError(t.durationErrorRange);
      return;
    }
    if (options.includes(parsed)) {
      setError(t.durationErrorDuplicate);
      return;
    }
    const next = [...options, parsed].sort((a, b) => a - b);
    setNewDuration('');
    persistOptions(next);
  };

  const handleRemove = (value: number) => {
    if (options.length <= 1) {
      setError(t.durationErrorMinOne);
      return;
    }
    persistOptions(options.filter((d) => d !== value));
  };

  const handleDefaultSelect = async (raw: string) => {
    setError('');
    setSuccess('');
    const next = raw === '' ? null : parseFloat(raw);
    setSaving(true);
    try {
      const res = await updateDefaultDuration(next as number, token);
      setDefaultDuration(res.default_duration);
      onDefaultChange?.(res.default_duration);
      setSuccess(t.durationDefaultUpdated);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(detailMessage(err, t.durationFailedUpdateDefault));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="duration-options-section">
      <h4>{t.durationSettingsHeading}</h4>
      <p className="duration-options-hint">
        {t.durationSettingsHint}
      </p>

      {error && <div className="error-message"><strong>{t.errorLabel2}</strong> {error}</div>}
      {success && <div className="success-message"><strong>✓</strong> {success}</div>}

      <ul className="duration-options-list">
        {options.map((d) => (
          <li key={d}>
            <span>{d === 1 ? t.oneHour : t.nHours(d)}</span>
            <button
              type="button"
              className="duration-remove-button"
              onClick={() => handleRemove(d)}
              disabled={saving || options.length <= 1}
              aria-label={t.durationRemoveAriaLabel(d)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <div className="default-duration-row">
        <label htmlFor="defaultDuration">{t.durationDefaultLabel}</label>
        <select
          id="defaultDuration"
          value={defaultDuration ?? ''}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleDefaultSelect(e.target.value)}
          disabled={saving || options.length === 0}
        >
          <option value="">{t.durationNoDefault}</option>
          {options.map((d) => (
            <option key={d} value={d}>
              {d === 1 ? t.oneHour : t.nHours(d)}
            </option>
          ))}
        </select>
      </div>

      <form className="duration-add-form" onSubmit={handleAdd} data-section="add">
        <input
          type="number"
          step="0.5"
          min="0.5"
          max="8"
          value={newDuration}
          onChange={(e) => setNewDuration(e.target.value)}
          placeholder={t.durationAddPlaceholder}
          disabled={saving}
        />
        <button type="submit" className="submit-button" disabled={saving}>
          {saving ? t.durationSavingButton : t.durationAddButton}
        </button>
      </form>
    </section>
  );
};

export default DurationSettings;
